import { useState, useEffect } from 'react'
import { autoLogin } from '../services/api'

interface AuthState {
  token: string | null
  shop: string | null
  isLoading: boolean
  isAuthenticated: boolean
}

// App Bridge v4 exposes window.shopify — no React provider needed
declare global {
  interface Window {
    shopify?: {
      idToken(): Promise<string>
      navigate?: (url: string) => void
      [key: string]: any
    }
  }
}

// localStorage is scoped to this app's origin, not per-shop. If the merchant
// switches stores in the same browser, the iframe loads the same origin and
// therefore the same localStorage — so a stale token/shop from the previous
// store must never be trusted for a different shop. Compare against the URL
// (the one thing Shopify always supplies fresh) before trusting anything.
function readInitialAuthState(): AuthState {
  const urlShop = new URLSearchParams(window.location.search).get('shop')
  const storedShop = localStorage.getItem('shop_domain')
  const storedToken = localStorage.getItem('auth_token')

  if (urlShop && storedShop && urlShop !== storedShop) {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('shop_domain')
    localStorage.removeItem('shop_host')
    return { token: null, shop: null, isLoading: true, isAuthenticated: false }
  }

  return {
    token: storedToken,
    shop: storedShop,
    isLoading: !storedToken,
    isAuthenticated: !!storedToken,
  }
}

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>(readInitialAuthState)

  useEffect(() => {
    if (state.isAuthenticated) return

    const searchParams = new URLSearchParams(window.location.search)
    const shop = searchParams.get('shop')
    const host = searchParams.get('host')

    if (!shop) {
      setState(s => (s.isLoading ? { ...s, isLoading: false } : s))
      return
    }

    let cancelled = false

    const finishLogin = (idToken?: string) =>
      autoLogin(shop, host ?? '', idToken)
        .then(({ data }) => {
          if (cancelled) return
          if (data.status === 'success') {
            localStorage.setItem('auth_token', data.token)
            localStorage.setItem('shop_domain', shop)
            localStorage.setItem('shop_host', host ?? '')
            setState({ token: data.token, shop, isLoading: false, isAuthenticated: true })
          } else if (data.status === 'redirect') {
            // Break out of Shopify Admin iframe so accounts.shopify.com can load
            const target = window.top ?? window
            target.location.href = data.redirect_url
          }
        })
        .catch(() => {
          if (!cancelled) setState(s => ({ ...s, isLoading: false }))
        })

    // Outside the real Shopify Admin iframe (i.e. `vite dev` on localhost),
    // App Bridge's idToken() has no parent frame to negotiate a session with
    // and never resolves. The backend only needs id_token to install a store
    // for the first time — an already-installed store's autoLogin succeeds on
    // `shop` alone — so in dev mode skip straight to it. Stripped from prod
    // builds since `import.meta.env.DEV` is inlined to `false` by Vite.
    if (import.meta.env.DEV) {
      finishLogin()
      return () => { cancelled = true }
    }

    if (!host) {
      setState(s => (s.isLoading ? { ...s, isLoading: false } : s))
      return
    }

    // Poll until App Bridge's CDN script has attached window.shopify, then run the auth flow once.
    const attempt = () => {
      if (cancelled) return

      if (!window.shopify) {
        setTimeout(attempt, 300)
        return
      }

      window.shopify.idToken().then(finishLogin).catch(() => {
        if (!cancelled) setState(s => ({ ...s, isLoading: false }))
      })
    }

    attempt()
    return () => { cancelled = true }
    // Only re-run when auth status actually changes — depending on the whole `state`
    // object here would re-fire on every setState above (new object reference each
    // time) and infinite-loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isAuthenticated])

  return state
}
